import React from "react"
import styled from "styled-components"
import { QuestionData } from '../models'
import { SecondaryHeader, Flex, PrimaryHeader, NavBackButton, BackIcon } from '../styles/global'
import Button from '@material-ui/core/Button'

interface Props {
  quiz: any
  shouldDisplay: any
}

export function QuizDisplay({ quiz, shouldDisplay }: Props) {
  return (
    <DisplayWrapper>
      <DisplayHeader>
        <NavBackButton startIcon={<BackIcon />} onClick={() => shouldDisplay(false)}>
          Back to Quizzes
        </NavBackButton>
        <Flex justify="space-between" alignItems="center">
          <PrimaryHeader style={{ margin: 0 }}>Quiz: {quiz.title}</PrimaryHeader>
          <StatusBadge>LAUNCHED / READ-ONLY</StatusBadge>
        </Flex>
      </DisplayHeader>

      <SectionWrapper>
        <SecondaryHeader>Quiz Details</SecondaryHeader>
        <DetailsCard>
          <DetailRow>
            <Label>Title</Label>
            <Value>{quiz.title}</Value>
          </DetailRow>
          <DetailRow>
            <Label>Description</Label>
            <Value>{quiz.description || "No description provided."}</Value>
          </DetailRow>
        </DetailsCard>
      </SectionWrapper>

      <SectionWrapper>
        <SecondaryHeader>Questions ({quiz.questions.length})</SecondaryHeader>
        <QuestionsGrid>
          {quiz.questions.map((questionItem: QuestionData, index: number) => (
            <QuestionCard key={questionItem.id}>
              <QuestionHeader>
                <QuestionNumber>Question {index + 1}</QuestionNumber>
              </QuestionHeader>
              <QuestionTitle>{questionItem.title}</QuestionTitle>

              <OptionsList>
                {['a', 'b', 'c', 'd', 'e', 'f'].map((key) => {
                  const val = (questionItem as any)[key];
                  if (!val) return null;
                  const isCorrect = questionItem.answer === key;
                  return (
                    <OptionItem key={key} isCorrect={isCorrect}>
                      <OptionLetter isCorrect={isCorrect}>{key.toUpperCase()}</OptionLetter>
                      <OptionText>{val}</OptionText>
                      {isCorrect && <CorrectIndicator>Correct Answer</CorrectIndicator>}
                    </OptionItem>
                  );
                })}
              </OptionsList>

              {questionItem.info_link && (
                <InfoLink href={questionItem.info_link} target="_blank" rel="noopener noreferrer">
                  Learn More → <UrlText>{questionItem.info_link}</UrlText>
                </InfoLink>
              )}
            </QuestionCard>
          ))}
        </QuestionsGrid>
      </SectionWrapper>

      <Footer>
        <Button
          variant="contained"
          color="primary"
          onClick={() => shouldDisplay(false)}
          style={{ borderRadius: '8px', padding: '10px 32px', fontWeight: 600 }}
        >
          Close View
        </Button>
      </Footer>
    </DisplayWrapper>
  );
}

const DisplayWrapper = styled.div`
  width: 100%;
  margin: 0;
  padding: 0 0 100px 0;
`;

const DisplayHeader = styled.div`
  margin-bottom: 40px;
`;



const StatusBadge = styled.div`
  background: #E2E8F0;
  color: #4A5568;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
`;

const SectionWrapper = styled.div`
  margin-bottom: 48px;
`;

const DetailsCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const DetailRow = styled.div`
  margin-bottom: 20px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`;

const Value = styled.div`
  font-size: 16px;
  color: #1a202c;
  line-height: 1.6;
`;

const QuestionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const QuestionCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const QuestionNumber = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const QuestionTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 24px 0;
  line-height: 1.4;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionItem = styled.div<{ isCorrect: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${props => props.isCorrect ? '#F0FFF4' : '#F7FAF8'};
  border: 1px solid ${props => props.isCorrect ? '#68D391' : '#EDF2F7'};
`;

const OptionLetter = styled.div<{ isCorrect: boolean }>`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-weight: 800;
  font-size: 14px;
  background: ${props => props.isCorrect ? '#48BB78' : '#E2E8F0'};
  color: ${props => props.isCorrect ? 'white' : '#718096'};
  margin-right: 16px;
`;

const OptionText = styled.div`
  font-size: 15px;
  color: #2D3748;
  flex: 1;
`;

const CorrectIndicator = styled.div`
  font-size: 11px;
  font-weight: 800;
  color: #2F855A;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InfoLink = styled.a`
  display: inline-block;
  margin-top: 24px;
  color: #3f51b5;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const UrlText = styled.span`
  font-weight: 400;
  color: #718096;
  margin-left: 8px;
  font-family: monospace;
  font-size: 13px;
`;

const Footer = styled.div`
  margin-top: 48px;
  display: flex;
  justify-content: center;
`;